import { isReasonCodesExtensionName, reasonCodesExtensionName } from '@/vantage/constants';
import reasonCodesExtension from '@/vantage/extensions/reason_codes.json';
import type { ExtensionSchema } from '@/vantage/types/Extension.types';
import type { ReasonCodeConditionAssignmentOutputV1 } from '@seeqdev/api/model/ReasonCodeConditionAssignmentOutputV1';

export type AssignedReasonCodeHierarchy = { id: string; name: string; levels: string[] };

export const REASON_CODE_AUTHOR_COLUMN_NAME = 'Author';

export type ReasonCodeRouting = {
  explicit: Record<string, string | undefined>;
  defaultHierarchyId?: string;
};

/** Builds the routing for a room's assignments; the row without a conditionId is the room default. */
export const reasonCodeRoutingFromAssignments = (
  assignments: ReasonCodeConditionAssignmentOutputV1[],
): ReasonCodeRouting => ({
  explicit: Object.fromEntries(
    assignments
      .filter((a) => a.conditionId && a.hierarchyCategoryId)
      .map((a) => [a.conditionId!.toLowerCase(), a.hierarchyCategoryId!]),
  ),
  defaultHierarchyId: assignments.find((a) => a.conditionId == null && a.hierarchyCategoryId)?.hierarchyCategoryId,
});

/** The hierarchy covering a condition: its explicit assignment wins, else the room default. */
export const reasonCodeHierarchyFor = (
  routing: ReasonCodeRouting | undefined,
  conditionId: string | undefined,
): string | undefined =>
  routing
    ? ((conditionId ? routing.explicit[conditionId.toLowerCase()] : undefined) ?? routing.defaultHierarchyId)
    : undefined;

// The backend resolves reasonCode.<category> by category name, so hierarchies sharing a level name share a column.
const buildColumns = (hierarchy: AssignedReasonCodeHierarchy): NonNullable<ExtensionSchema['uiConfig']>['columns'] => {
  if (!hierarchy.levels.length) return [];
  const levelColumns = hierarchy.levels.map((level) => ({
    colDefName: level,
    colDefType: 'string' as const,
    colDefFilterType: 'set' as const,
    category: level,
  }));
  return [
    ...levelColumns,
    {
      colDefName: REASON_CODE_AUTHOR_COLUMN_NAME,
      colDefType: 'string' as const,
      colDefFilterType: 'set' as const,
      category: hierarchy.levels[0],
    },
  ];
};

// The hierarchy id and levels are stashed in extensionSpecificConfiguration for the ReasonCodes view.
const buildReasonCodesSchema = (base: ExtensionSchema, hierarchy: AssignedReasonCodeHierarchy): ExtensionSchema => ({
  ...base,
  name: reasonCodesExtensionName(hierarchy.id),
  uiConfig: {
    ...base.uiConfig,
    columns: buildColumns(hierarchy),
    extensionSpecificConfiguration: {
      ...base.uiConfig?.extensionSpecificConfiguration,
      hierarchyId: hierarchy.id,
      hierarchyLabels: hierarchy.levels,
    },
    iconRepresentation: {
      ...base.uiConfig?.iconRepresentation,
      // Hierarchy names are user data, not i18n keys, so set `label` and clear `labelTranslationKey`.
      discreteStates: base.uiConfig?.iconRepresentation?.discreteStates?.map((state) => ({
        ...state,
        labelTranslationKey: undefined,
        label: hierarchy.name,
      })),
    },
  },
});

export const deriveReasonCodeExtensions = (hierarchies: AssignedReasonCodeHierarchy[]): ExtensionSchema[] =>
  hierarchies.map((hierarchy) => buildReasonCodesSchema(reasonCodesExtension as ExtensionSchema, hierarchy));

export const mergeReasonCodeExtensions = (
  persistedExtensions: ExtensionSchema[],
  legacyReasonCodeTableId: string | undefined,
  hierarchies: AssignedReasonCodeHierarchy[] | undefined,
): ExtensionSchema[] => {
  // Feature inactive (flag off or not loaded): passthrough.
  if (hierarchies === undefined) return persistedExtensions;
  // A legacy room (reason codes materialized table present) keeps its persisted button alongside derived ones.
  const withoutReplaced = legacyReasonCodeTableId
    ? persistedExtensions
    : persistedExtensions.filter((ext) => !isReasonCodesExtensionName(ext.name));
  return [...withoutReplaced, ...deriveReasonCodeExtensions(hierarchies)];
};
